import {type RefObject, useEffect, useState} from 'react'

/**
 * A lead stated as a share of the scroll container, rather than a flat distance: one figure
 * cannot fit both the board and a cell a few hundred pixels deep.
 */
export type PagingLead = {
  axis: 'inline' | 'block'
  fraction: number
  minPixels: number
  maxPixels: number
}

export function leadPixels(container: HTMLElement | null, lead: PagingLead): number | null {
  if (container === null) return null

  const extent = lead.axis === 'inline' ? container.clientWidth : container.clientHeight
  // Unlaid-out, so measuring would fix the lead at the floor for the observer's life.
  if (extent === 0) return null

  return Math.round(Math.min(Math.max(extent * lead.fraction, lead.minPixels), lead.maxPixels))
}

export function useLeadMargin(
  containerRef: RefObject<HTMLElement | null>,
  lead: PagingLead | undefined,
): number | null {
  const [leadMargin, setLeadMargin] = useState<number | null>(null)

  useEffect(() => {
    const container = containerRef.current
    if (lead === undefined || container === null) return

    const measure = () => {
      const next = leadPixels(container, lead)
      setLeadMargin(current => (next === null || next === current ? current : next))
    }

    // Synchronous, so the first observer is built with the real lead rather than the
    // fallback and then rebuilt a frame later.
    measure()

    // A resize delivers during layout, and committing there rebuilds the paging observer
    // in the same phase. Coalesced to a frame so a drag-resize costs one rebuild per frame.
    let frame = 0
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    })
    observer.observe(container)

    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [containerRef, lead])

  return lead === undefined ? null : leadMargin
}
